import React, { useEffect, useRef, useState } from 'react';

const CAR_PATH = 'M24.7276 0.5C19.7176 0.5 15.0901 3.005 12.3038 7.17875L1.87506 22.7675C0.647734 24.6056 -0.00495598 26.7673 6.44165e-05 28.9775V53.8213C-0.00684993 55.4859 0.543046 57.1051 1.5623 58.4213C2.58154 59.7375 4.01162 60.6751 5.62506 61.085L15.3526 63.5487C16.0522 66.919 17.8896 69.9462 20.5566 72.1223C23.2237 74.2983 26.5579 75.491 30.0001 75.5C36.9451 75.5 42.7426 70.6963 44.4151 64.25H79.3351C81.0076 70.6963 86.8051 75.5 93.7501 75.5C100.695 75.5 106.493 70.6963 108.165 64.25H112.5C116.603 64.25 120 60.8525 120 56.75V43.04C119.996 39.6974 118.876 36.452 116.817 33.8191C114.757 31.1863 111.877 29.3172 108.634 28.5087L88.3576 23.3525L73.7101 5.8925C72.3089 4.20642 70.5538 2.84928 68.5694 1.91736C66.585 0.985435 64.4199 0.501542 62.2276 0.5H24.7276ZM24.7276 8H41.2501V23H10.7813L18.5176 11.3975V11.2812C19.197 10.2608 20.1205 9.42614 21.2043 8.85313C22.2882 8.28012 23.4979 7.98687 24.7238 8H24.7276ZM48.7501 8H62.2276C64.4513 8 66.5476 8.9825 67.9688 10.6962L78.2813 23H48.7501V8ZM7.50006 30.5H85.7813L106.875 35.7725C108.494 36.1742 109.93 37.1101 110.95 38.4289C111.971 39.7477 112.517 41.3724 112.5 43.04V56.75H108.165C106.493 50.3037 100.695 45.5 93.7501 45.5C86.8051 45.5 81.0076 50.3037 79.3351 56.75H44.4151C42.7426 50.3037 36.9451 45.5 30.0001 45.5C23.3513 45.5 17.7676 49.895 15.8213 55.9287L7.50006 53.825V30.5ZM30.0001 53C34.1888 53 37.5001 56.3112 37.5001 60.5C37.5001 64.6888 34.1888 68 30.0001 68C25.8113 68 22.5001 64.6888 22.5001 60.5C22.5001 56.3112 25.8113 53 30.0001 53ZM93.7501 53C97.9388 53 101.25 56.3112 101.25 60.5C101.25 64.6888 97.9388 68 93.7501 68C89.5613 68 86.2501 64.6888 86.2501 60.5C86.2501 56.3112 89.5613 53 93.7501 53Z';

const VIEW_HEIGHT = 76;

interface CarLoaderProps {
  /** Real upload progress (0-100). Used when `indeterminate` is false. */
  progress?: number;
  /** When true, the fill loops up and down to signal "working" without a known percentage (offline). */
  indeterminate?: boolean;
  color?: string;
  emptyColor?: string;
  width?: number;
}

const CarLoader: React.FC<CarLoaderProps> = ({
  progress = 0,
  indeterminate = false,
  color = 'var(--infovist-primary, #D40020)',
  emptyColor = '#DEDDDC',
  width = 120,
}) => {
  const clipId = useRef(`car-loader-clip-${Math.random().toString(36).slice(2)}`);
  const [loopValue, setLoopValue] = useState(0);
  const [shouldAnimate, setShouldAnimate] = useState(false);

  useEffect(() => {
    if (!indeterminate) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      setLoopValue((prev) => (prev >= 100 ? 0 : prev + 5));
    }, 80);

    return () => clearInterval(intervalId);
  }, [indeterminate]);

  useEffect(() => {
    if (indeterminate) {
      setShouldAnimate(false);
      return undefined;
    }

    const raf = requestAnimationFrame(() => setShouldAnimate(true));
    return () => cancelAnimationFrame(raf);
  }, [indeterminate]);

  const fillPercent = indeterminate
    ? loopValue
    : Math.max(0, Math.min(100, progress));

  const filledHeight = (VIEW_HEIGHT * fillPercent) / 100;
  const clipY = VIEW_HEIGHT - filledHeight;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={(width * VIEW_HEIGHT) / 120}
      viewBox="0 0 120 76"
      fill="none"
    >
      <defs>
        <clipPath id={clipId.current}>
          <rect
            x="0"
            y={clipY}
            width="120"
            height={filledHeight}
            style={{ transition: shouldAnimate ? 'y 0.3s ease, height 0.3s ease' : 'none' }}
          />
        </clipPath>
      </defs>

      <path d={CAR_PATH} fill={emptyColor} />

      <g clipPath={`url(#${clipId.current})`}>
        <path d={CAR_PATH} fill={color} />
      </g>
    </svg>
  );
};

export default CarLoader;
